/**
 * The viewport — the honest lens over the boot-proven renderer (ADR-005 §2,
 * UX review §3.5): a persistent render loop, orbit navigation, an IBL-filled
 * light rig whose key sits OFF the viewing azimuth (so axial twist reads as a
 * vertical terminator — v2's rig could not show it), a shadow catcher placed
 * from the subject's MEASURED bounds (foot plant is judgeable), and clip
 * playback. Deliberately chrome-free — this module is the scene, not the show
 * around it. Track bindings are pre-validated so a clip that reaches no bones
 * fails loudly (ADR-001 §7), never plays to silence.
 * Depends on: three, three/webgpu (PMREM), three/addons (OrbitControls,
 * RoomEnvironment), lens (the rig geometry as data).
 * Depended on by: app shell (main.ts), first-light demo.
 */
import {
  AnimationMixer,
  Box3,
  BufferGeometry,
  CanvasTexture,
  CircleGeometry,
  Color,
  DirectionalLight,
  Group,
  LineBasicMaterial,
  LineSegments,
  Mesh,
  MeshBasicMaterial,
  NeutralToneMapping,
  PCFSoftShadowMap,
  PerspectiveCamera,
  PlaneGeometry,
  Raycaster,
  Scene,
  RenderTarget,
  ShadowMaterial,
  Vector2,
  Vector3,
  type AnimationClip,
  type SkinnedMesh,
  type Object3D,
} from 'three';
import { PMREMGenerator, type WebGPURenderer } from 'three/webgpu';
import { OrbitControls } from 'three/addons/controls/OrbitControls.js';
import { RoomEnvironment } from 'three/addons/environments/RoomEnvironment.js';
import { isViewportDebris, trackBindsOn } from '@harryhausen/adapter-three';
import { squareFovDeg } from '../package/hero-png.js';
import { type HeadshotLens, type SubjectMeasure } from '../package/headshot-frame.js';
import {
  CATCHER_OPACITY,
  DEFAULT_VIEW_DIRECTION,
  ENVIRONMENT_INTENSITY,
  KEY_DIRECTION,
  KEY_INTENSITY,
  POOL_COLOR,
  azimuthDeg,
  azimuthSeparationDeg,
  catcherPlacement,
  neutralDarkGreyInverse,
  lensPlacement,
} from './lens.js';
import { createPoseTool, type PoseTool, type PoseToolConfig } from './pose-tool.js';
import { createPinTool, type PinTool, type PinToolConfig } from './pin-tool.js';

/** Live playback counters, mutated every frame — the smoke test's heartbeat. */
export interface ViewportProbe {
  frames: number;
  clipTimeSec: number;
  /**
   * Is the mixer being held still? The viewport's OWN state, not an inference
   * from the clock — a clip that happens to be at rest reads identical to one
   * that is paused, so "did that gesture resume playback?" is not answerable
   * from the outside without this.
   */
  paused: boolean;
  /**
   * How many times present() ran. Every present resets the clip clock, so an
   * UNRELATED edit re-presenting is measurable here as a count that moved
   * when it should not have (Codex P2, #61 r4).
   */
  presents: number;
}

export interface PresentInfo {
  /** Clip tracks whose node AND property both resolve in the presented object. */
  readonly boundTracks: number;
  /** Tracks that would be inert at playback — enumerated, never silent. */
  readonly unresolvedTracks: readonly string[];
}

/**
 * The lens's facts, MEASURED from the live renderer and scene objects — never
 * echoed from the constants that configured them (a probe that repeats its
 * own input proves wiring, not truth). Separation is taken against the
 * CURRENT camera: at boot that is the framed default view; after the user
 * orbits it reports wherever they actually are.
 */
export interface LensEvidence {
  /** An environment texture is live — the fill that varies with the normal. */
  readonly environment: boolean;
  readonly environmentIntensity: number;
  /** 'neutral' (Khronos PBR Neutral) or the raw code if something rewired it. */
  readonly toneMappingName: string;
  readonly outputColorSpace: string;
  readonly pixelRatio: number;
  readonly keyAzimuthDeg: number;
  /** The §3.5 number: key-to-camera azimuthal separation. v2 measured 0.000000°. */
  readonly keyViewSeparationDeg: number;
  readonly shadowsEnabled: boolean;
  /** Subject meshes actually casting — v2 had zero (a depth target held nothing). */
  readonly shadowCasters: number;
  readonly catcherReceives: boolean;
  readonly catcherY: number;
}

export interface Viewport {
  readonly probe: ViewportProbe;
  /** Measure the lens as it stands — the smoke's §3.5 evidence surface. */
  lensEvidence(): LensEvidence;
  /**
   * Register the per-frame driver for DERIVED bones (ADR-028 correctives):
   * runs inside the render loop after the mixer samples and before the frame
   * renders, so helpers follow their drivers through every presentation path
   * — playback, pose projection, scrubbing. One slot (one owner); null
   * withdraws it. The shell also calls its driver synchronously wherever it
   * parses scene TRS outside the loop (the export door) — this hook covers
   * the frames, not those reads.
   */
  setFrameDriver(fn: (() => void) | null): void;
  /** Dev experiment: restore temporary presentation edits immediately after render. */
  setJointEditing(config: PoseToolConfig | null): void;
  setRenderPreview(fn: (() => (() => void)) | null): void;
  /** Isolated editor overlays; never attached to the exportable subject. */
  editingSurface(): { camera: PerspectiveCamera; canvas: HTMLCanvasElement; overlays: Group; navigation(enabled: boolean): void; preserveView(): () => void };
  /** Show an object (replacing any prior subject) and optionally play a clip on it. */
  present(root: Object3D, clip: AnimationClip | null): PresentInfo;
  /**
   * The ONE door between "clip the app chose" and "clip the mixer samples"
   * (ADR-017 §3): every playable — first present, re-present, pause→replay —
   * plays `filter(original)`. The subject keeps the ORIGINAL clip, so a filter
   * swap recomputes from it (never from a previously filtered copy), and node
   * rest state is never written: the mixer's cached bindings keep their clean
   * originals across the swap, so stopAllAction restores rests untouched by
   * the offset — the mixer-birth disease is unrepresentable here, not guarded.
   * Playing: the action rebuilds immediately. Paused: the swap lands on the
   * stored clip and the next play wears it. Null restores unfiltered playback.
   */
  setClipDisplayFilter(filter: ((clip: AnimationClip) => AnimationClip) | null): void;
  /**
   * Free the GPU resources of a subtree that is DONE — geometries, materials,
   * their textures, and skinned-mesh bone textures. `scene.remove` alone frees
   * nothing: the renderer holds allocations until an explicit dispose, so a
   * session that swaps subjects (ten consecutive clip drops re-import the model
   * ten times) accumulates every previous subject in GPU memory until the
   * context dies. Refuses the LIVE subject — dispose only after presenting its
   * replacement. Call it after overlay consumers (pose tool) have re-homed:
   * their shared-source geometries must have left this tree.
   */
  disposeDetached(root: Object3D): void;
  /**
   * The hero take (ADR-033 phase 6): the subject alone, rendered through the
   * SAME lens as the screen into a square offscreen target with a
   * transparent clear, and read back. The stage — pool, catcher, grid, the
   * tools' helpers — is hidden for the one render and restored; the camera is
   * the live one at aspect 1 (a centre crop of the view, the same vertical
   * extent). Never `preserveDrawingBuffer` on the main context. Rows come
   * back in the backend's order; `flipY` says whether they are bottom-first.
   */
  captureSquare(size: number, lens?: HeadshotLens): Promise<{ rgba: Uint8Array; size: number; flipY: boolean; hiddenDebris: number }>;
  /**
   * Measure the presented subject for a headshot (ADR-035 §3.1): its floor,
   * height and horizontal centre from the REST bounds (the bind-pose geometry
   * through the world matrix — World's scale rule), and the world Y of the
   * head bone `pickHead` chooses among its node names at the frame as it
   * stands (World's naming rule lives with the formula, not here). Rig units.
   */
  measureSubject(pickHead: (names: readonly string[]) => string | null): SubjectMeasure;
  /**
   * HOLD the live camera at a lens (the guided intake's first frame, INTAKE_GUIDED_FLOW §3.3): the
   * placement `captureSquare` gives its clone (`lensPlacement`), applied to the live camera, the orbit
   * target and the key. A HOLD, not a move: while it stands the loop re-seats the camera every frame and
   * the orbit is off, so no tool that restores a saved view (a rerun's preserveView, a hand's focus)
   * can carry the first frame away (measured 2026-10-03: the kobold's elbow rerun did). Null releases
   * it, putting back the camera, target and key as they stood before the first hold.
   */
  holdLens(lens: HeadshotLens | null): void;
  /**
   * The head as the MESH has it (the guided first frame, INTAKE_GUIDED_FLOW §3.3 amended): the
   * world box of every skinned vertex whose heaviest influence is the head bone `pickHead` names or
   * a bone under it (ears, hair, a hood all ride the head), at the pose as it stands. Null when no
   * head bone is named or no vertex follows it.
   */
  measureHead(pickHead: (names: readonly string[]) => string | null): { readonly min: { x: number; y: number; z: number }; readonly max: { x: number; y: number; z: number }; readonly vertices: number; readonly headBone: string } | null;
  /**
   * Suppress every helper drawn over the subject — the editing overlays (hair skull, pose gizmo, elbow
   * pins) and every stamped debris node under the subject (zone tint, hand markers, weight and wire
   * overlays) — at render time, whatever the tools believe they are showing. The guided intake's
   * `guided()` predicate: no register can leak an overlay into the first impression. False restores.
   */
  setOverlaysSuppressed(on: boolean): void;
  /**
   * Pause or resume clip sampling. In pose mode the mixer must NOT sample
   * (ADR-007 §5.3 — nothing competes with the pose layer), or a pose.set on a
   * clip-driven bone is overwritten next frame (Codex P1, #54). Pausing STOPS
   * the action, which restores every bound property to its original (rest)
   * state — §5.2: with no active clip the pose layer projects over the rest
   * pose ALONE, never over a frozen mid-animation frame (Codex P1 round 2:
   * a frozen frame would also poison the projector's lazy rest snapshot).
   * Resuming replays the clip from the top. Orbit and render keep running.
   */
  setPaused(paused: boolean): void;
  /**
   * HOLD the clip at one time (ADR-035 §3.3: World seats frame zero for a
   * still hero). Unlike pause — which stops the action and shows the REST
   * pose — the action stays bound and sampled at `seconds`, and the loop
   * stops advancing it until the next present, replay, or setPaused. A frame
   * count is not a time: two bodies settling "two frames in" land on
   * different poses; a held time lands on the same one.
   */
  holdAt(seconds: number): void;
  /**
   * Offer or withdraw the pose gesture (ADR-006 §13.2: mode scopes tools).
   * Pass a config to offer it against the CURRENT subject; null withdraws it
   * entirely — outside pose mode the gesture is unrepresentable, not guarded.
   * Offering with no subject presented refuses loudly (§8).
   */
  setPoseTool(config: PoseToolConfig | null): void;
  /** The live tool, if offered — the smoke's evidence surface. */
  getPoseTool(): PoseTool | null;
  /**
   * Offer or withdraw the PIN gesture (ADR-022 D1). Null withdraws it.
   *
   * THE TWO CLICK-TOOLS ARE MUTUALLY EXCLUSIVE, and this seam owns that rather
   * than leaving it to whoever calls last. Both listen for a click on the same
   * canvas, so offering both means one click can mean two things — the exact
   * ambiguity ADR-006 §13.2 forbids. Arming the pin tool therefore WITHDRAWS
   * the pose gizmo and disarming RESTORES it, from the config the caller last
   * offered: a person who armed pinning in pose mode gets their gizmo back when
   * they are done, without the shell having to remember to re-offer it.
   */
  setPinTool(config: PinToolConfig | null): void;
  /** The live pin tool, if armed — the smoke's evidence surface. */
  getPinTool(): PinTool | null;
  /**
   * The ZONE WAND's click (ADR-031 §3.2 T3): a press and release on the canvas
   * (no orbit travel) that hits the subject's skinned mesh reports the
   * triangle's index — the Zone's own face numbering. Null disarms. A trusted
   * click only: the wand edits the Document.
   */
  setFacePicker(onPick: ((face: number) => void) | null): void;
  /**
   * Project a world point to screen pixels, or null when it is behind the
   * camera. The marker layer needs it to say where a pin IS on screen (so a
   * test can aim a real click at one), and only the viewport knows the camera.
   */
  projectPoint(p: { x: number; y: number; z: number }): { x: number; y: number } | null;
  /**
   * Look closely at one small thing, from above. The Hand Studio needs this: a
   * hand is a few centimetres of a whole character, and asking someone to judge
   * which branch is a thumb while the hand is forty pixels across is asking them
   * to guess. From above because that is the view a hand's digits separate in —
   * the palm plane faces the camera and the thumb's offset is a real distance
   * rather than a foreshortened one.
   *
   * Near/far are re-seated from the RADIUS, because the default frustum is cut
   * for a whole body and would clip a hand-sized subject away entirely.
   */
  focusOn(center: { x: number; y: number; z: number }, radius: number): void;
  /** Put the camera at `eye`, looking at `target` — the e2e's screenshot door (ADR-038 J2's weight shots). */
  viewFrom(eye: { x: number; y: number; z: number }, target: { x: number; y: number; z: number }): void;
}

// Design-token stage: --bg-deep ground, --shelf borders for the grid.
const STAGE_BACKGROUND = '#0a0a0a';
const GRID_MAJOR = '#2a2a2a';
const GRID_MINOR = '#1a1a1a';

/**
 * Would each track actually drive something at playback? The rule is the
 * adapter's `trackBindsOn` (track-binding.ts) — shared with the library
 * binder so a clip counted as bound there is a clip presented here.
 */
function validateBindings(root: Object3D, clip: AnimationClip): PresentInfo {
  const unresolved: string[] = [];
  for (const track of clip.tracks) {
    if (!trackBindsOn(root, track.name)) unresolved.push(track.name);
  }
  if (clip.tracks.length > 0 && unresolved.length === clip.tracks.length) {
    throw new Error(
      `clip "${clip.name}" binds to NOTHING in "${root.name}" — ` +
        `all ${clip.tracks.length} tracks unresolved (first: ${unresolved[0]})`,
    );
  }
  return { boundTracks: clip.tracks.length - unresolved.length, unresolvedTracks: unresolved };
}

/**
 * A stage token, pre-compensated so it DISPLAYS as itself. The node pipeline
 * tone-maps the WHOLE framebuffer in one output pass — material.toneMapped is
 * not honored (measured 2026-07-25: flagged grid lines still exited crushed),
 * so there is exactly ONE mechanism for chrome surviving the specimen's tone
 * map: feed the inverse. Neutral-specific — the smoke pins the operator.
 */
function stageGrey(token: string): Color {
  const fed = neutralDarkGreyInverse(new Color(token).r); // grey: one channel is all three
  return new Color().setRGB(fed, fed, fed);
}

/**
 * The stage grid, hand-built from two single-color line sets. NOT a
 * GridHelper: its vertex-colored line material renders NOTHING in r185's
 * node pipeline (measured 2026-07-25 — zero grid pixels ever drawn on main
 * in either backend, while a single-color LineBasicMaterial draws fine).
 * Same geometry as GridHelper(size, divisions): axis lines in the major
 * color, the rest minor.
 */
function buildStageGrid(size: number, divisions: number): Group {
  const half = size / 2;
  const step = size / divisions;
  const major: Vector3[] = [];
  const minor: Vector3[] = [];
  for (let i = 0; i <= divisions; i++) {
    const p = -half + i * step;
    const into = Math.abs(p) < step / 2 ? major : minor;
    into.push(new Vector3(p, 0, -half), new Vector3(p, 0, half));
    into.push(new Vector3(-half, 0, p), new Vector3(half, 0, p));
  }
  const grid = new Group();
  for (const [points, token] of [
    [major, GRID_MAJOR],
    [minor, GRID_MINOR],
  ] as const) {
    grid.add(
      new LineSegments(new BufferGeometry().setFromPoints([...points]), new LineBasicMaterial({ color: stageGrey(token) })),
    );
  }
  return grid;
}

/** Radial white→black falloff — the stage pool's alpha, melting into the void. */
function makeRadialFalloffTexture(): CanvasTexture {
  const size = 128;
  const surface = document.createElement('canvas');
  surface.width = size;
  surface.height = size;
  const ctx = surface.getContext('2d');
  if (ctx === null) throw new Error('2d canvas unavailable — cannot build the stage pool falloff');
  const gradient = ctx.createRadialGradient(size / 2, size / 2, 0, size / 2, size / 2, size / 2);
  gradient.addColorStop(0, '#ffffff');
  gradient.addColorStop(0.55, '#a0a0a0');
  gradient.addColorStop(1, '#000000');
  ctx.fillStyle = gradient;
  ctx.fillRect(0, 0, size, size);
  return new CanvasTexture(surface);
}

export function createViewport(renderer: WebGPURenderer, canvas: HTMLCanvasElement): Viewport {
  // Renderer-level lens facts (§3.5), set before the first frame. Khronos PBR
  // Neutral over ACESFilmic: this tool JUDGES generated PBR, and Neutral was
  // designed to preserve albedo hue/saturation where ACES shifts them — the
  // honest choice for a review lens. Pixel ratio capped at 2: device pixels
  // without paying 3x-retina fill cost.
  renderer.toneMapping = NeutralToneMapping;
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
  renderer.shadowMap.enabled = true;
  renderer.shadowMap.type = PCFSoftShadowMap;

  const scene = new Scene();
  // Compensated like every stage surface (see stageGrey) — uncompensated, the
  // token displayed at ~0/255 and the subject's dark silhouette edge drowned.
  scene.background = stageGrey(STAGE_BACKGROUND);

  const camera = new PerspectiveCamera(50, canvas.clientWidth / Math.max(1, canvas.clientHeight), 0.01, 100);
  camera.position.set(0, 1.6, 3);

  // The fill is an IBL, not a constant: environment irradiance varies with
  // the surface normal — that variation IS the form a flat ambient destroys —
  // and it is the only fill that lights a metalness=1 surface at all (v2
  // rendered metals black). fromScene is sync here because boot already
  // awaited renderer.init() (the generator itself throws if not — fail-loud).
  const pmrem = new PMREMGenerator(renderer);
  scene.environment = pmrem.fromScene(new RoomEnvironment(), 0.04).texture;
  scene.environmentIntensity = ENVIRONMENT_INTENSITY;
  pmrem.dispose();

  // The key: geometry from lens.ts (tested — azimuth OFF the viewing axis so
  // a VERTICAL terminator reads twist), wattage taste. Position/target/shadow
  // frustum are seated per-subject in frameSubject from measured bounds.
  const keyDirection = new Vector3(KEY_DIRECTION.x, KEY_DIRECTION.y, KEY_DIRECTION.z).normalize();
  const key = new DirectionalLight('#ffffff', KEY_INTENSITY);
  key.castShadow = true;
  key.shadow.mapSize.set(2048, 2048);
  key.shadow.bias = -0.0004;
  key.shadow.normalBias = 0.02;
  key.position.copy(keyDirection).multiplyScalar(6);
  scene.add(key, key.target);

  // THE STAGE IS CHROME, NOT SPECIMEN. The tone map exists to judge PBR
  // materials; Neutral's flare suppression (out = 6.25x² below linear 0.08)
  // crushes everything this dark to ≤10/255 — measured 2026-07-25: the grid
  // vanished and the catcher's black-on-black shadow was invisible. Chrome
  // survives via stageGrey compensation; the specimen never gets any.
  //
  // The pool is the surface the contact shadow reads against, faded radially
  // into the void; the catcher above it holds ONLY the received shadow.
  const pool = new Mesh(
    new CircleGeometry(1, 48),
    new MeshBasicMaterial({
      color: stageGrey(POOL_COLOR),
      transparent: true,
      alphaMap: makeRadialFalloffTexture(),
      depthWrite: false,
    }),
  );
  pool.rotation.x = -Math.PI / 2;
  pool.renderOrder = 1; // deterministic: pool first, shadow composites over it
  scene.add(pool);

  const catcher = new Mesh(new PlaneGeometry(2, 2), new ShadowMaterial({ opacity: CATCHER_OPACITY }));
  catcher.rotation.x = -Math.PI / 2;
  catcher.receiveShadow = true;
  catcher.renderOrder = 2;
  scene.add(catcher);

  scene.add(buildStageGrid(10, 20));

  const controls = new OrbitControls(camera, canvas);
  controls.enableDamping = true;
  controls.target.set(0, 1, 0);

  const resize = (): void => {
    renderer.setSize(canvas.clientWidth, canvas.clientHeight, false);
    camera.aspect = canvas.clientWidth / Math.max(1, canvas.clientHeight);
    camera.updateProjectionMatrix();
  };
  window.addEventListener('resize', resize);
  // The canvas lives in a flex center whose size moves when workspace zones
  // open/close (ADR-015) — no window resize fires for that, so the canvas
  // observes its OWN box. Belt and suspenders with the window listener.
  new ResizeObserver(resize).observe(canvas);
  resize();

  const probe: ViewportProbe = { frames: 0, clipTimeSec: 0, presents: 0, paused: false };
  let subject: {
    root: Object3D;
    mixer: AnimationMixer | null;
    /** What the mixer samples — `clipFilter(original)` when a filter is set. */
    clip: AnimationClip | null;
    /** What the app chose — the filter's input, never mutated, never played raw while filtered. */
    original: AnimationClip | null;
  } | null = null;
  let clipFilter: ((clip: AnimationClip) => AnimationClip) | null = null;
  let paused = false;
  /** A time the clip is held at (holdAt); cleared by present, replay, setPaused. */
  let held = false;
  let poseTool: PoseTool | null = null;
  /** The pose config last OFFERED — held so the pin tool can borrow the canvas and give it back. */
  let poseConfig: PoseToolConfig | null = null;
  let jointConfig: PoseToolConfig | null = null;
  let pinTool: PinTool | null = null;
  /** The zone wand's click listeners, removed by calling it. */
  let facePicker: (() => void) | null = null;
  /**
   * The bone the gizmo was on, held across a rebuild. The pin gesture borrows
   * the canvas and the gizmo is disposed for the duration, so without this the
   * bone a person had selected came back UNSELECTED — and nothing else was
   * going to fix it, because the panel's disarm is local state that notifies
   * nobody (Codex P2, #94). A tool taken away and returned is returned as it was.
   */
  let poseSelection: string | null = null;

  /**
   * (Re-)instantiate the pose gizmo from the config last offered. ONE
   * construction path, shared by `setPoseTool` and by the pin tool handing the
   * canvas back — a second `createPoseTool` call site is how the two tools
   * would come to disagree about which of them owns a click.
   */
  const offerPoseTool = (): void => {
    // THE SELECTION SURVIVES THE HAND-OFF (Codex P2, #94). Arming the pin
    // gesture disposes the gizmo and disarming builds a fresh one, whose
    // `selected` is null — so a bone that was selected before pinning lost its
    // gizmo afterwards, and stayed that way until some unrelated store change
    // happened to re-offer the tool. The panel's disarm is local state and
    // notifies nobody, so nothing else was ever going to put it back. A tool
    // that is taken away and returned should be returned as it was.
    if (poseTool !== null) poseSelection = poseTool.probe.selected;
    poseTool?.dispose();
    poseTool = null;
    const offered = jointConfig ?? poseConfig;
    if (offered === null) return;
    if (subject === null) {
      throw new Error('setPoseTool: no subject presented — a pose tool over nothing is not an offer (§8)');
    }
    // While pinning is armed the gizmo stays withdrawn; the config is held and
    // instantiated when the pin tool is put away.
    if (pinTool !== null) return;
    poseTool = createPoseTool(
      {
        scene,
        camera,
        domElement: canvas,
        subject: subject.root,
        setOrbitEnabled: (enabled) => {
          controls.enabled = enabled;
        },
      },
      offered,
    );
    if (poseSelection !== null) poseTool.select(poseSelection);
    poseTool.update(); // seat the handles before the first frame renders them
  };
  let shadowCasters = 0;

  let lastMs: number | null = null;
  let frameDriver: (() => void) | null = null;
  let renderPreview: (() => (() => void)) | null = null;
  renderer.setAnimationLoop((timeMs: number) => {
    const deltaSec = lastMs === null ? 0 : (timeMs - lastMs) / 1000;
    lastMs = timeMs;
    if (subject?.mixer && !paused && !held) {
      subject.mixer.update(deltaSec);
      probe.clipTimeSec += deltaSec;
    }
    // Derived bones follow their drivers AFTER the mixer writes and BEFORE
    // anything reads world matrices this frame (ADR-028's live door).
    frameDriver?.();
    poseTool?.update(); // handles ride the bones' world matrices
    controls.update();
    seatHeld();
    const restorePreview = renderPreview?.();
    // The guided intake's predicate, applied where every overlay meets the eye: hidden for this render, put back after.
    const suppressed: Object3D[] = [];
    if (overlaysSuppressed) {
      if (editorOverlays.visible) suppressed.push(editorOverlays);
      subject?.root.traverse((node) => {
        if (node.visible && isViewportDebris(node)) suppressed.push(node);
      });
      for (const node of suppressed) node.visible = false;
    }
    try { renderer.render(scene, camera); } finally { for (const node of suppressed) node.visible = true; restorePreview?.(); }
    probe.frames += 1;
  });

  const editorOverlays = new Group();
  scene.add(editorOverlays);
  let overlaysSuppressed = false;
  /** What the first hold replaced; null = not holding. */
  let heldView: { position: Vector3; target: Vector3; fov: number; near: number; far: number; key: Vector3; keyTarget: Vector3; orbit: boolean } | null = null;
  let heldPlacement: ReturnType<typeof lensPlacement> | null = null;
  const seatHeld = (): void => {
    const at = heldPlacement;
    if (at === null) return;
    camera.fov = at.fovDeg;
    camera.near = at.near;
    camera.far = at.far;
    camera.position.set(at.camera.x, at.camera.y, at.camera.z);
    camera.up.set(0, 1, 0);
    camera.lookAt(at.target.x, at.target.y, at.target.z);
    camera.updateProjectionMatrix();
    controls.target.set(at.target.x, at.target.y, at.target.z);
    key.position.set(at.key.x, at.key.y, at.key.z);
    key.target.position.set(at.keyTarget.x, at.keyTarget.y, at.keyTarget.z);
    key.target.updateMatrixWorld();
  };
  function focusOn(center: { x: number; y: number; z: number }, radius: number): void {
    const safeRadius = Math.max(radius, 1e-4);
    const at = new Vector3(center.x, center.y, center.z);
    controls.target.copy(at);
    // Above, and pulled slightly back along +Z so it is a three-quarter top
    // rather than a pure plan view — a pure plan flattens the very offset the
    // thumb is identified by.
    camera.position.copy(at).add(new Vector3(0, safeRadius * 3.2, safeRadius * 1.6));
    camera.near = Math.max(safeRadius / 100, 0.0001);
    camera.far = Math.max(safeRadius * 400, 50);
    camera.updateProjectionMatrix();
    controls.update();
  }

  function frameSubject(root: Object3D): void {
    const box = new Box3().setFromObject(root);
    if (box.isEmpty()) {
      throw new Error(`presented object "${root.name}" has no measurable bounds — nothing to frame`);
    }
    const center = box.getCenter(new Vector3());
    const size = box.getSize(new Vector3());
    const radius = Math.max(size.x, size.y, size.z) / 2;
    controls.target.copy(center);
    camera.position
      .copy(center)
      .add(
        new Vector3(DEFAULT_VIEW_DIRECTION.x, DEFAULT_VIEW_DIRECTION.y, DEFAULT_VIEW_DIRECTION.z)
          .normalize()
          .multiplyScalar(Math.max(radius * 2.6, 0.5)),
      );
    camera.near = Math.max(radius / 100, 0.001);
    camera.far = Math.max(radius * 100, 50);
    camera.updateProjectionMatrix();

    // Key, catcher, and shadow frustum derive from the SAME measurement as
    // the framing (§3.5: framing facts come from measured bounds — v2 framed
    // every model as an assumed 1.8m human and discarded its own Box3).
    key.position.copy(center).add(keyDirection.clone().multiplyScalar(radius * 4));
    key.target.position.copy(center);
    key.target.updateMatrixWorld();
    const placement = catcherPlacement({ minY: box.min.y, radius });
    catcher.position.set(center.x, placement.y, center.z);
    catcher.scale.setScalar(placement.halfSize); // PlaneGeometry(2,2): scale = half the edge
    pool.position.set(center.x, placement.y - 0.001, center.z);
    pool.scale.setScalar(placement.halfSize); // CircleGeometry(1): scale = radius
    const shadowCam = key.shadow.camera;
    const extent = placement.halfSize * 1.1;
    shadowCam.left = -extent;
    shadowCam.right = extent;
    shadowCam.top = extent;
    shadowCam.bottom = -extent;
    shadowCam.near = radius * 0.5;
    shadowCam.far = radius * 10;
    shadowCam.updateProjectionMatrix();
  }

  return {
    probe,
    focusOn,
    viewFrom(eye, target) {
      controls.target.set(target.x, target.y, target.z);
      camera.position.set(eye.x, eye.y, eye.z);
      camera.near = 0.01;
      camera.updateProjectionMatrix();
      controls.update();
    },
    editingSurface: () => ({ camera, canvas, overlays: editorOverlays, navigation: (enabled) => { controls.enabled = enabled; },
      preserveView: () => {
        const position = camera.position.clone(), rotation = camera.quaternion.clone(), target = controls.target.clone();
        const near = camera.near, far = camera.far;
        return () => { camera.position.copy(position); camera.quaternion.copy(rotation); camera.near = near; camera.far = far;
          camera.updateProjectionMatrix(); controls.target.copy(target); controls.update(); };
      },
    }),
    setJointEditing(config) {
      if (config && pinTool) throw new Error('Finish placing the hand or tail pin before fixing elbows.');
      jointConfig = config === null ? null : { ...config, readOnly: true };
      offerPoseTool();
    },
    setRenderPreview(fn) { renderPreview = fn; },
    setFrameDriver(fn) {
      frameDriver = fn;
    },
    async captureSquare(size, lens) {
      if (subject === null) throw new Error('hero take: nothing is presented');
      // Registered as the OUTPUT target: the renderer then applies the tone
      // map and the output colour space exactly as for the canvas
      // (Renderer.isOutputTarget), so the take carries the screen's lens.
      const target = new RenderTarget(size, size, { samples: 4, depthBuffer: true });
      const hidden: Object3D[] = [];
      const keep = new Set<Object3D>([subject.root, key, key.target]);
      for (const child of scene.children) {
        if (!keep.has(child) && child.visible) {
          child.visible = false;
          hidden.push(child);
        }
      }
      // The stage's helpers ride UNDER the subject — wireframe, weight overlay,
      // zone tint, hand markers — every one stamped (markViewportDebris, the
      // #140-era rule that ended the prefix list). A character-only take hides
      // the stamped nodes too (Codex P1, #154).
      let hiddenDebris = 0;
      subject.root.traverse((node) => {
        if (node.visible && isViewportDebris(node)) {
          node.visible = false;
          hidden.push(node);
          hiddenDebris += 1;
        }
      });
      const background = scene.background;
      const keyPosition = key.position.clone();
      const keyTarget = key.target.position.clone();
      const square = camera.clone();
      // The inscribed square: the live vertical FOV on a landscape viewport, the
      // live HORIZONTAL extent on a portrait one (Codex P2, #154).
      square.fov = squareFovDeg(camera.fov, camera.aspect);
      square.aspect = 1;
      if (lens !== undefined) {
        // A lens handed in (the headshot, ADR-035): the CLONE is placed from
        // it and the live orbit is never touched; the key follows the camera
        // onto the facing axis (Codex P1, #162) and is put back with the
        // scene, before the pixel read. ONE placement, shared with holdLens.
        const at = lensPlacement(lens, keyPosition);
        square.fov = at.fovDeg;
        square.position.set(at.camera.x, at.camera.y, at.camera.z);
        square.up.set(0, 1, 0);
        square.lookAt(at.target.x, at.target.y, at.target.z);
        square.near = at.near;
        square.far = at.far;
        key.position.set(at.key.x, at.key.y, at.key.z);
        key.target.position.set(at.keyTarget.x, at.keyTarget.y, at.keyTarget.z);
        key.target.updateMatrixWorld();
      }
      square.updateProjectionMatrix();
      // The scene is borrowed for the SYNCHRONOUS render only and put back
      // before the asynchronous pixel read: nothing the author does during the
      // read (hide an overlay, delete a zone) can be undone by the restore,
      // because the restore has already happened (Codex P2, #154 round three).
      const restoreScene = (): void => {
        scene.background = background;
        for (const o of hidden) o.visible = true;
        hidden.length = 0;
        key.position.copy(keyPosition);
        key.target.position.copy(keyTarget);
        key.target.updateMatrixWorld();
      };
      try {
        scene.background = null;
        renderer.setOutputRenderTarget(target);
        renderer.setRenderTarget(target);
        renderer.setClearColor(0x000000, 0);
        renderer.render(scene, square);
        restoreScene();
        renderer.setRenderTarget(null);
        renderer.setOutputRenderTarget(null);
        const read = (await renderer.readRenderTargetPixelsAsync(target, 0, 0, size, size)) as ArrayLike<number>;
        const rgba = read instanceof Uint8Array ? read : Uint8Array.from(read);
        const isWebGPU = (renderer.backend as { isWebGPUBackend?: boolean }).isWebGPUBackend === true;
        return { rgba, size, flipY: !isWebGPU, hiddenDebris };
      } finally {
        restoreScene(); // a refusal before the render still puts the stage back; after it, a no-op
        renderer.setRenderTarget(null);
        renderer.setOutputRenderTarget(null);
        target.dispose();
      }
    },
    holdLens(lens) {
      if (lens === null) {
        if (heldView === null) return;
        heldPlacement = null;
        controls.enabled = heldView.orbit;
        camera.position.copy(heldView.position);
        camera.fov = heldView.fov;
        camera.near = heldView.near;
        camera.far = heldView.far;
        camera.updateProjectionMatrix();
        controls.target.copy(heldView.target);
        key.position.copy(heldView.key);
        key.target.position.copy(heldView.keyTarget);
        key.target.updateMatrixWorld();
        heldView = null;
        controls.update();
        return;
      }
      heldView ??= {
        position: camera.position.clone(),
        target: controls.target.clone(),
        fov: camera.fov,
        near: camera.near,
        far: camera.far,
        key: key.position.clone(),
        keyTarget: key.target.position.clone(),
        orbit: controls.enabled,
      };
      // The key is turned from where it stood BEFORE the first hold, so a re-hold never turns it twice.
      heldPlacement = lensPlacement(lens, heldView.key);
      controls.enabled = false;
      seatHeld();
    },
    setOverlaysSuppressed(on) {
      overlaysSuppressed = on;
    },
    measureHead(pickHead) {
      if (subject === null) return null;
      subject.root.updateMatrixWorld(true);
      const names: string[] = [];
      const byName = new Map<string, Object3D>();
      subject.root.traverse((node) => {
        if (isViewportDebris(node) || node.name === '' || byName.has(node.name)) return;
        names.push(node.name);
        byName.set(node.name, node);
      });
      const headBone = pickHead(names);
      const head = headBone === null ? undefined : byName.get(headBone);
      if (head === undefined || headBone === null) return null;
      const under = new Set<Object3D>();
      head.traverse((n) => under.add(n));
      const box = new Box3();
      const v = new Vector3();
      let vertices = 0;
      subject.root.traverse((node) => {
        if (isViewportDebris(node) || !(node as SkinnedMesh).isSkinnedMesh || !node.visible) return;
        const mesh = node as SkinnedMesh;
        const g = mesh.geometry as BufferGeometry;
        const index = g.getAttribute('skinIndex');
        const weight = g.getAttribute('skinWeight');
        const position = g.getAttribute('position');
        if (index === undefined || weight === undefined || position === undefined) return;
        const bones = mesh.skeleton.bones;
        const owns = bones.map((b) => under.has(b));
        if (!owns.some(Boolean)) return;
        for (let i = 0; i < position.count; i++) {
          let best = -1;
          let bestW = 0;
          for (let k = 0; k < 4; k++) {
            const w = weight.getComponent(i, k);
            if (w > bestW) {
              bestW = w;
              best = index.getComponent(i, k);
            }
          }
          if (best < 0 || owns[best] !== true) continue;
          mesh.getVertexPosition(i, v);
          v.applyMatrix4(mesh.matrixWorld);
          box.expandByPoint(v);
          vertices += 1;
        }
      });
      if (vertices === 0 || box.isEmpty()) return null;
      return { min: { x: box.min.x, y: box.min.y, z: box.min.z }, max: { x: box.max.x, y: box.max.y, z: box.max.z }, vertices, headBone };
    },
    measureSubject(pickHead) {
      if (subject === null) throw new Error('headshot: nothing is presented');
      subject.root.updateMatrixWorld(true);
      // REST bounds, never a cached skinned box (Codex P2, #162): three reuses
      // a SkinnedMesh's box from the pose it was first computed in, so a
      // crouch taken later would read the earlier pose's floor and height.
      // Each mesh's GEOMETRY box (bind pose) through its world matrix is what
      // World's scale rule reads — the loaded mesh at rest; only the head
      // reading is live. The helpers under the root are stamped debris and
      // are left out of the measurement as they are left out of the take.
      const box = new Box3();
      const meshBox = new Box3();
      const names: string[] = [];
      const byName = new Map<string, Object3D>();
      subject.root.traverse((node) => {
        if (isViewportDebris(node)) return;
        if (node.name !== '' && !byName.has(node.name)) {
          names.push(node.name);
          byName.set(node.name, node);
        }
        if (node instanceof Mesh) {
          const geometry = node.geometry as BufferGeometry;
          if (geometry.boundingBox === null) geometry.computeBoundingBox();
          if (geometry.boundingBox !== null) box.union(meshBox.copy(geometry.boundingBox).applyMatrix4(node.matrixWorld));
        }
      });
      if (box.isEmpty()) throw new Error(`headshot: "${subject.root.name}" has no measurable bounds — nothing to frame`);
      const headBone = pickHead(names);
      const headY = headBone === null ? null : byName.get(headBone)!.getWorldPosition(new Vector3()).y;
      return {
        floorY: box.min.y,
        heightUnits: box.max.y - box.min.y,
        headY,
        headBone,
        centerX: (box.min.x + box.max.x) / 2,
        centerZ: (box.min.z + box.max.z) / 2,
      };
    },
    lensEvidence: () => ({
      environment: scene.environment !== null,
      environmentIntensity: scene.environmentIntensity,
      toneMappingName: renderer.toneMapping === NeutralToneMapping ? 'neutral' : `code:${renderer.toneMapping}`,
      outputColorSpace: String(renderer.outputColorSpace),
      pixelRatio: renderer.getPixelRatio(),
      keyAzimuthDeg: azimuthDeg(key.position.clone().sub(controls.target)),
      keyViewSeparationDeg: azimuthSeparationDeg(
        key.position.clone().sub(controls.target),
        camera.position.clone().sub(controls.target),
      ),
      shadowsEnabled: renderer.shadowMap.enabled,
      shadowCasters,
      catcherReceives: catcher.receiveShadow,
      catcherY: catcher.position.y,
    }),
    present(root, clip): PresentInfo {
      // Re-presenting the SAME subject (a rebuilt authored clip after a key
      // edit or undo, §13.4) swaps only what the mixer samples — the camera
      // belongs to the sitting, not to the clip, and must not yank.
      const sameSubject = subject?.root === root;
      if (subject !== null) {
        subject.mixer?.stopAllAction();
        if (!sameSubject) scene.remove(subject.root);
      }
      const info = clip === null ? { boundTracks: 0, unresolvedTracks: [] } : validateBindings(root, clip);
      if (!sameSubject) {
        // Every subject mesh casts — v2's rig rendered a depth target every
        // frame that held NOTHING (castShadow on zero meshes). The catcher is
        // the only receiver for now; skinned self-shadowing waits until its
        // acne can be judged deliberately, not shipped as noise. A same-subject
        // re-present changes no meshes — casters, framing, and the catcher all
        // hold from the first presentation.
        shadowCasters = 0;
        root.traverse((node) => {
          if ((node as { isMesh?: boolean }).isMesh === true) {
            node.castShadow = true;
            shadowCasters += 1;
          }
        });
        scene.add(root);
        frameSubject(root);
        // A NEW subject while a lens is held: what a release returns to is THIS subject's framing, not the
        // previous character's saved view (Codex P2, #241). The hold itself is re-fitted by its owner.
        if (heldView !== null) {
          heldView = {
            position: camera.position.clone(),
            target: controls.target.clone(),
            fov: heldView.fov, // frameSubject leaves the fov alone; the camera's is the hold's lens
            near: camera.near,
            far: camera.far,
            key: key.position.clone(),
            keyTarget: key.target.position.clone(),
            orbit: heldView.orbit,
          };
        }
      }
      let mixer: AnimationMixer | null = null;
      const playable = clip !== null && clipFilter !== null ? clipFilter(clip) : clip;
      if (playable !== null) {
        mixer = new AnimationMixer(root);
        mixer.clipAction(playable).play();
      }
      subject = { root, mixer, clip: playable, original: clip };
      probe.clipTimeSec = 0;
      probe.presents += 1;
      held = false; // a new presentation runs; a hold belongs to the clip that was held
      return info;
    },
    setClipDisplayFilter(filter) {
      clipFilter = filter;
      if (subject === null || subject.original === null || subject.mixer === null) return;
      const previous = subject.clip;
      const next = filter !== null ? filter(subject.original) : subject.original;
      subject.clip = next;
      if (!paused) {
        // Same mixer on purpose: its cached bindings hold the clean rest
        // originals; stop restores them, the new action reuses them. A fresh
        // mixer here would record whatever the bones held as "rest" — the
        // #61 r5–r7 disease this seam exists to make unrepresentable. The
        // uncache runs AFTER the new action binds (shared bindings stay
        // referenced, keeping their saved originals) and only on this branch:
        // while paused the old action still owns the binding cache, and the
        // next present replaces the whole mixer anyway.
        subject.mixer.stopAllAction();
        subject.mixer.clipAction(next).play();
        probe.clipTimeSec = 0; // replays from the top, and the counter says so
        if (previous !== null && previous !== next) subject.mixer.uncacheClip(previous);
      }
    },
    disposeDetached(root) {
      if (subject?.root === root) {
        throw new Error('disposeDetached: this is the live subject — present its replacement first');
      }
      root.traverse((node) => {
        const drawable = node as {
          isMesh?: boolean;
          isLine?: boolean;
          isPoints?: boolean;
          isSkinnedMesh?: boolean;
          geometry?: { dispose(): void };
          material?: unknown;
          skeleton?: { dispose?: () => void };
        };
        if (drawable.isMesh !== true && drawable.isLine !== true && drawable.isPoints !== true) return;
        drawable.geometry?.dispose();
        const materials = Array.isArray(drawable.material) ? drawable.material : [drawable.material];
        for (const material of materials) {
          if (material === null || material === undefined) continue;
          // Textures hang off arbitrary material slots (map, normalMap, …) —
          // enumerate rather than name them, so a new slot cannot silently leak.
          for (const value of Object.values(material as Record<string, unknown>)) {
            if ((value as { isTexture?: boolean } | null)?.isTexture === true) {
              (value as { dispose(): void }).dispose();
            }
          }
          (material as { dispose(): void }).dispose();
        }
        // The skeleton's bone matrices live in a GPU texture of their own.
        if (drawable.isSkinnedMesh === true) drawable.skeleton?.dispose?.();
      });
    },
    holdAt(seconds) {
      if (!subject?.mixer || subject.clip === null) throw new Error('holdAt: nothing is playing to hold');
      const action = subject.mixer.clipAction(subject.clip);
      if (!action.isRunning()) action.play();
      // setTime samples every bound property at that time; the loop then
      // leaves the mixer alone until something replays or re-presents.
      subject.mixer.setTime(seconds);
      probe.clipTimeSec = seconds;
      held = true;
    },
    setPaused(next) {
      probe.paused = next;
      held = false;
      if (next === paused) return;
      paused = next;
      if (!subject?.mixer || subject.clip === null) return;
      if (next) {
        // stop() restores each binding's ORIGINAL state (three's PropertyMixer
        // restoreOriginalState on useCount→0) — the rest pose, captured when
        // the action first bound. This is the §5.2 guarantee, not a freeze.
        subject.mixer.stopAllAction();
      } else {
        subject.mixer.clipAction(subject.clip).play();
        probe.clipTimeSec = 0; // replays from the top — the counter says so
      }
    },
    setPoseTool(config) {
      // REMEMBERED, so the pin tool can borrow the canvas and give it back.
      poseConfig = config;
      offerPoseTool();
    },
    getPoseTool: () => poseTool,
    setPinTool(config) {
      if (config && jointConfig) throw new Error('Finish placing elbow pins before starting another pin tool.');
      pinTool?.dispose();
      pinTool = null;
      if (config === null) {
        // Hand the canvas back: re-offer the gizmo the caller last asked for,
        // through the same door, so there is one construction path for it.
        offerPoseTool();
        return;
      }
      if (subject === null) {
        throw new Error('setPinTool: no subject presented — a pin gesture over nothing is not an offer (§8)');
      }
      // The gizmo goes away FIRST: one canvas, one meaning per click — and its
      // selection is remembered, because it is coming back.
      if (poseTool !== null) poseSelection = poseTool.probe.selected;
      poseTool?.dispose();
      poseTool = null;
      pinTool = createPinTool({ camera, domElement: canvas, subject: subject.root }, config);
    },
    getPinTool: () => pinTool,
    setFacePicker(onPick) {
      facePicker?.();
      facePicker = null;
      canvas.style.cursor = '';
      if (onPick === null) return;
      if (subject === null) throw new Error('setFacePicker: no subject presented — nothing to pick a face on');
      const root = subject.root;
      const ray = new Raycaster();
      let pressAt: { x: number; y: number } | null = null;
      const down = (e: PointerEvent): void => {
        pressAt = { x: e.clientX, y: e.clientY };
      };
      const up = (e: PointerEvent): void => {
        const start = pressAt;
        pressAt = null;
        if (start === null || !e.isTrusted || Math.hypot(e.clientX - start.x, e.clientY - start.y) > 5) return;
        const rect = canvas.getBoundingClientRect();
        ray.setFromCamera(new Vector2(((e.clientX - rect.left) / rect.width) * 2 - 1, -((e.clientY - rect.top) / rect.height) * 2 + 1), camera);
        root.updateWorldMatrix(true, true);
        const hit = ray.intersectObject(root, true).find((h) => (h.object as { isSkinnedMesh?: boolean }).isSkinnedMesh === true && h.faceIndex != null);
        if (hit !== undefined) onPick(hit.faceIndex!);
      };
      canvas.addEventListener('pointerdown', down);
      canvas.addEventListener('pointerup', up);
      canvas.style.cursor = 'crosshair';
      facePicker = () => {
        canvas.removeEventListener('pointerdown', down);
        canvas.removeEventListener('pointerup', up);
      };
    },
    projectPoint(p) {
      const v = new Vector3(p.x, p.y, p.z).project(camera);
      if (v.z > 1) return null; // behind the camera — not aimable
      const rect = canvas.getBoundingClientRect();
      return {
        x: rect.left + ((v.x + 1) / 2) * rect.width,
        y: rect.top + ((1 - v.y) / 2) * rect.height,
      };
    },
  };
}
